import React, { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { LibraryItem } from './types';
import { LibraryService } from '@/lib/library-service';
import { Dialog } from '@/components/ui/dialog';
import { CreateItemForm } from './CreateItemForm';

interface EditLibraryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  libraryItem: LibraryItem;
  onItemUpdated: (updatedItem: LibraryItem) => void;
}

export function EditLibraryItemModal({
  isOpen,
  onClose,
  libraryItem,
  onItemUpdated
}: EditLibraryItemModalProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Handle updating of the item
  const handleItemUpdated = async (updatedItem: LibraryItem) => {
    setIsSaving(true);
    
    try {
      // We receive the full updated item from CreateItemForm
      // Pass only the updateable fields to updateItem
      const itemToUpdate = {
        type: updatedItem.type,
        data: updatedItem.data,
        title: updatedItem.title,
        description: updatedItem.description,
        tags: updatedItem.tags,
        location: updatedItem.location,
        thumbnailUrl: updatedItem.thumbnailUrl
      };
      
      // Update the item in the library
      const success = await LibraryService.updateItem(libraryItem.id, itemToUpdate);
      
      if (success) {
        // If successful, pass the updated item back to the parent
        // We're combining the updated fields with the original item
        const resultItem: LibraryItem = {
          ...libraryItem,
          ...itemToUpdate,
          updatedAt: new Date()
        };
        
        // Notify parent component
        onItemUpdated(resultItem);
        
        // Show success toast
        toast({
          title: "Item atualizado",
          description: "Item atualizado na biblioteca com sucesso!",
        });
        
        // Close the modal
        onClose();
      } else {
        throw new Error("Falha ao atualizar o item");
      }
    } catch (error) {
      console.error('Erro ao atualizar item na biblioteca:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o item na biblioteca.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <CreateItemForm 
      isOpen={isOpen}
      itemType={libraryItem.type as any}
      onClose={onClose}
      onItemCreated={handleItemUpdated}
      existingItem={libraryItem}
      isEditMode={true}
    />
  );
} 